import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { styled } from "@mui/material/styles";
import TablePagination from "@mui/material/TablePagination";
import { Loader } from "@egovernments/digit-ui-react-components";
import { formatTimestamp } from "../../../../MISReportComponent/revenueReport/helper/revenueFormHelper";
import { TableSortLabel } from "@mui/material";
import { ArrowDownward } from "@mui/icons-material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const getPendingDays = (date) => {
  if (!date) return "-";
  const parsedDate = dayjs(date, "DD-MM-YYYY");
  return dayjs().diff(parsedDate, "day");
};

const useStyles = makeStyles({
  table: {
    minWidth: 800,
  },
  tableRow: {
    backgroundColor: "#1F4A7C",
    color: "#fff",
  },
  tableCell: {
    backgroundColor: "#f2f2f2",
    padding: "10px",
    fontWeight: "bold",
    border: "1px solid #ddd",
  },
  blueText: {
    color: "#1F4A7C",
  },
});

const headerData = [
  { id: "slno", label: "Sr. No", sortable: true },
  { id: "applicationNo", label: "Application Number", sortable: true },
  { id: "riskType", label: "Risk Type", sortable: true },
  { id: "applicationType", label: "Module Name", sortable: true },
  { id: "lastCreatedTime", label: "Last Updated", sortable: true },
  { id: "pendingTime", label: "Pending Days", sortable: true },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1f4a7c",
    color: theme.palette.common.white,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  ":hover": {
    backgroundColor: "#1f4a7c1c",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EmployeePendingFiles = ({ applications, tenantId, onApplicationSelect }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowData, setRowData] = useState([]);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState("lastCreatedTime");
  const [order, setOrder] = useState("desc");

  useEffect(() => {
    if (applications && tenantId) {
      getRowData(applications, tenantId);
    }
  }, [tenantId, applications]);

  const getRowData = async (applications, tenantId) => {
    try {
      setIsLoading(true);
      const queryObj = {
        applicationNo: applications.join(","),
      };
      const res = await Digit.OBPSService.dashboardReportData(tenantId, queryObj);
      const reportData = res?.CommonSearchRequest || [];
      setRowData(reportData);
    } catch (err) {
      console.log("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTakeAction = (item) => {
    const sourceQuery = "?source=dashboard";

    switch (item?.businessService) {
      case "BPAREG":
        Digit.SessionStorage.del("EMP_LTP");
        Digit?.SessionStorage.del("isDocValidated");
        Digit?.SessionStorage.del("isNotingValidated");
        if (onApplicationSelect) {
          Digit.SessionStorage.set("DASHBOARD_ID", item?.applicationNo);
          onApplicationSelect(item);
        }
        break;

      case "BPA_MAPSUB_DA":
      case "BPA_MAPSUB_AVP":
      case "BPA_MAPPLINTH_DA":
      case "BPA_PURCHASEFAR_DA":
        Digit.SessionStorage.del("EMP_MAPSUB");
        Digit?.SessionStorage.del("isDocValidated");
        Digit?.SessionStorage.del("isNotingValidated");
        if (onApplicationSelect) {
          Digit.SessionStorage.set("DASHBOARD_ID", item?.applicationNo);
          onApplicationSelect(item);
        }
        break;

      case "BPA_FAR_DA":
      case "BPA_FAR_AVP":
        if (onApplicationSelect) {
          Digit.SessionStorage.set("DASHBOARD_ID", item?.applicationNo);
          onApplicationSelect(item);
        }
        break;

      case "BPA_LANDUSE_DA":
      case "BPA_LANDUSE_AVP":
      case "BPA_KYP_DA":
        Digit.SessionStorage.del("EMP_LANDUSE");
        Digit?.SessionStorage.del("isDocValidated");
        Digit?.SessionStorage.del("isNotingValidated");
        if (onApplicationSelect) {
          Digit.SessionStorage.set("DASHBOARD_ID", item?.applicationNo);
          onApplicationSelect(item);
        }
        break;

      default:
        if (item?.businessService?.startsWith("BPA_NOC")) {
          if (onApplicationSelect) {
            Digit.SessionStorage.set("DASHBOARD_ID", item?.applicationNo);
            onApplicationSelect(item);
          }
        } else {
          console.log("Unhandled Business Service", item?.businessService);
        }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getComparator = (order, orderBy) => {
    return (a, b) => {
      let aValue;
      let bValue;

      if (orderBy === "slno") {
        aValue = rowData.indexOf(a);
        bValue = rowData.indexOf(b);
      } else {
        aValue = a?.[orderBy] ? a[orderBy].toString().toLowerCase() : "";
        bValue = b?.[orderBy] ? b[orderBy].toString().toLowerCase() : "";
      }

      if (orderBy === "lastCreatedTime") {
        aValue = dayjs(a?.lastCreatedTime, "DD-MM-YYYY").valueOf();
        bValue = dayjs(b?.lastCreatedTime, "DD-MM-YYYY").valueOf();
      }

      if (orderBy === "pendingTime") {
        aValue = getPendingDays(a?.lastCreatedTime);
        bValue = getPendingDays(b?.lastCreatedTime);
      }

      if (order === "desc") {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    };
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = rowData.slice().sort(getComparator(order, orderBy));

  return (
    <React.Fragment>
      {isLoading ? (
        <Loader />
      ) : (
        <Paper sx={{ overflow: "hidden", marginY: 2 }}>
          <TableContainer>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow className={classes.tableRow}>
                  {headerData.map((column) => (
                    <StyledTableCell key={column.id}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>{column.label}</span>

                        {column.sortable && (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={() => handleSort(column.id)}
                            sx={{
                              color: "white !important",
                              "& .MuiTableSortLabel-icon": {
                                opacity: "1 !important",
                                color: "white !important",
                              },
                              "&:hover": {
                                color: "white !important",
                              },
                            }}
                            IconComponent={(props) => (
                              <ArrowDownward
                                {...props}
                                sx={{
                                  color: "white !important",
                                  opacity: 1,
                                  fontSize: "16px",
                                }}
                              />
                            )}
                          />
                        )}
                      </div>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody style={{ backgroundColor: "#fbfaff" }}>
                {sortedData &&
                  sortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                    <StyledTableRow key={row.serialNo}>
                      <StyledTableCell>{index + 1 + page * rowsPerPage}</StyledTableCell>
                      <StyledTableCell
                        style={{
                          color: "#1976d2",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        onClick={() => handleTakeAction(row)}
                      >
                        {row?.applicationNo || "-"}
                      </StyledTableCell>
                      <StyledTableCell>{row?.riskType || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row?.applicationType || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row?.lastCreatedTime || "-"}</StyledTableCell>
                      <StyledTableCell>{getPendingDays(row?.lastCreatedTime)}</StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 75, 100]}
            component="div"
            count={rowData?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </React.Fragment>
  );
};

export default EmployeePendingFiles;
